import { watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useAuthenticationStore } from '@cplug/frontend-shared-domains/domain/authentication'
import { isSessionRestoreComplete, isSessionRestoring } from './useSessionRestoreState'
import { useFirebaseAuth } from '~/shared/firebase/composables/useFirebaseAuth'
import { CPBaseFirebaseGateway } from '~/shared/gateways/CPBaseFirebaseGateway'
import { useTenantStoreReset } from '~/domain/dashboard/store/composables/useTenantStoreReset'

export function useAuthObserver (): void {
  const authPath = '/auth'
  const companyChooserPath = '/company'
  const loginPath = '/login'
  const termsPath = '/terms'
  const authStore = useAuthenticationStore()
  const { companies, logged } = storeToRefs(authStore)
  const router = useRouter()
  const route = useRoute()
  const firebaseAuth = useFirebaseAuth()

  useTenantStoreReset()

  function checkAuthAndRedirect (): void {
    if (!isSessionRestoreComplete.value || isSessionRestoring.value) {
      return
    }

    const hasAuthParamsInURL =
      route?.query?.access_token || route?.query?.refresh_token ||
      route?.query?.setup_company_id || route?.query?.company_id
    if (hasAuthParamsInURL) {
      return
    }

    if (route?.path === loginPath || route?.path?.startsWith(authPath)) {
      return
    }
    if (route?.path?.startsWith(termsPath)) {
      return
    }
    if (route?.meta?.isStoreRoute) {
      return
    }

    let loginRedirection: string | undefined
    let preserveRedirect = true

    if (companies.value.length === 0 && !route?.path?.startsWith(companyChooserPath)) {
      loginRedirection = companyChooserPath
    }

    if (!logged.value) {
      loginRedirection = loginPath
      preserveRedirect = false
      CPBaseFirebaseGateway.destroyAll()
      firebaseAuth.signOut?.()?.catch(() => {})
    }

    if (loginRedirection == null) {
      return
    }

    if (route.path === loginRedirection || route.path?.startsWith(`${loginRedirection}/`) === true) {
      return
    }

    router.push({
      path: loginRedirection,
      query: preserveRedirect ? { redirect: route.fullPath } : undefined
    })
  }

  watch(
    [logged, companies, (): string => route.path, (): string => route.fullPath, isSessionRestoring, isSessionRestoreComplete],
    (): void => {
      checkAuthAndRedirect()
    },
    { immediate: true }
  )
}
